// FragmentSearchResult.js
/**
 * @fileoverview Class to unify fragment search results from bfd-server and dataservice.
 */

/**
 * @typedef {import('BMapsModel').DecodedFragment} DecodedFragment
 */

import { calculateMolecularWeight, countHeavyAtoms } from '../util/chem_utils';

export class FragmentSearchResult {
    static calculatePoseStatistics(searchResults) {
        for (const searchResult of searchResults) {
            const statsByProjectCase = statsForOneSuggestionRow(searchResult);
            for (const [projectCase, stats] of Object.entries(statsByProjectCase)) {
                const perCaseInfo = searchResult.getPerCaseInfo(projectCase);
                perCaseInfo.poseStatistics = stats;
            }
        }
    }

    /**
     * Make a key for a fragment search result row.
     * The expected input is either:
     *   - a fragment decoded from binary data + attachment info
     *   - a search result (could be json search result from bfd-server or a FragmentSearchResult)
     * @param {{
     *     frag?: DecodedFragment,
     *     searchResult?: FragmentSearchResult | {
     *         name: string, modType: string, fragAtomName?: string,
     *         fragDirectionAtomName?: string, srcDirectionAtomName?: string
     *     },
     *     attachmentInfo?: {
     *         modType: string,
     *         fragAtomName?: string,
     *         fragDirectionAtomName?: string,
     *         srcDirectionAtomName?: string,
     *     } | null
     * }}
     * @returns {string}
     */
    static makeKey({ frag, searchResult, attachmentInfo }={}) {
        let name;
        let attachInfoToUse = attachmentInfo;
        if (frag) {
            name = frag.baseFrag.name;
        } else if (searchResult) {
            name = searchResult.name;
            // attachment info matches ModificationCmds makeDataServiceSuggestions
            if (!attachInfoToUse) {
                attachInfoToUse = {
                    modType: searchResult.modType,
                    srcDirectionAtomName: searchResult.srcDirectionAtomName,
                    fragAtomName: searchResult.fragAtomName,
                    fragDirectionAtomName: searchResult.fragDirectionAtomName,
                };
            }
        } else {
            throw new Error('FragmentSearchResult.makeKey requires either frag or searchResult');
        }

        const { modType, fragAtomName } = attachInfoToUse || {};
        let key = `${modType}-${name}`;
        if (fragAtomName) key += `-${fragAtomName}`;
        // @todo do we need to add fragDirectionAtomName to the key???
        // Key must include fragment name so direction atoms are per-fragment
        // May need to address when rolling up link types into a single row
        return key;
    }

    constructor(obj={}) {
        this.origin = obj.origin; // bfd-server | dataservice
        this.modType = obj.modType; // Near | Grow | etc
        this.frags = obj.frags || [];
        this.name = obj.name;
        this.mwt = obj.mwt;
        this.projectCaseDetails = obj.projectCaseDetails || {};
        // Bond vector atom names tracked for assembly
        // the src "fromAtom" isn't captured here, as it would require datapath adjustments
        /** Bond vector toAtom on the compound being modified */
        this.srcDirectionAtomName = obj.srcDirectionAtomName;
        /** Bond vector fromAtom on the fragment being merged */
        this.fragAtomName = obj.fragAtomName;
        /** Bond vector toAtom on the fragment being merged */
        this.fragDirectionAtomName = obj.fragDirectionAtomName;
    }

    addFrag(frag) {
        this.frags.push(frag);

        // Update project case details
        if (frag.projectCase) {
            const perCaseInfo = this.getPerCaseInfo(frag.projectCase);
            const existingBindingInfo = perCaseInfo.bestPoseInfo;
            const newBindingInfo = new BestPoseBindingInfo(frag);
            if (!existingBindingInfo || newBindingInfo.isBetterThan(existingBindingInfo)) {
                perCaseInfo.bestPoseInfo = newBindingInfo;
            }
        }
    }

    getPerCaseInfo(projectCase) {
        let perCaseInfo = this.projectCaseDetails[projectCase];
        if (!perCaseInfo) {
            perCaseInfo = {
                bestPoseInfo: null,
                poseStatistics: null,
            };
            this.projectCaseDetails[projectCase] = perCaseInfo;
        }
        return perCaseInfo;
    }

    recalculateBestPoses(bestPoseOrderField) {
        // Single pass over the frags, tracking the best pose per project-case.
        const bestByCase = new Map();
        for (const frag of this.frags) {
            const info = new BestPoseBindingInfo(frag);
            const current = bestByCase.get(frag.projectCase);
            if (!current || info.isBetterThan(current, bestPoseOrderField)) {
                bestByCase.set(frag.projectCase, info);
            }
        }
        for (const [projectCase, perCaseInfo] of Object.entries(this.projectCaseDetails)) {
            perCaseInfo.bestPoseInfo = bestByCase.get(projectCase);
        }
    }

    /**
     * @param {Array} frags Array of fragments to search for the best pose.
     * @param {{
     *     projectCase: string?,
     *     bestPoseOrderField: string,
     * }} options Options for finding the best pose.
     * @returns {BestPoseBindingInfo} The best pose binding information found.
     */
    static findBestPose(frags, { projectCase, bestPoseOrderField }) {
        let bestBindInfo;
        for (const frag of frags) {
            if (projectCase && frag.projectCase !== projectCase) continue;
            const newBindingInfo = new BestPoseBindingInfo(frag);
            if (!bestBindInfo || newBindingInfo.isBetterThan(bestBindInfo, bestPoseOrderField)) {
                bestBindInfo = newBindingInfo;
            }
        }
        return bestBindInfo;
    }
}

export class BfdServerFragmentSearchResult extends FragmentSearchResult {
    static syncResultFragments(searchResults, fragments, caseData) {
        const fragMap = new Map();
        for (const frag of fragments) {
            frag.setCaseData(caseData);
            frag.projectCase = caseData?.mapCase?.projectCase;
            if (frag.poseSerialNo != null) fragMap.set(frag.poseSerialNo, frag);
        }

        for (const searchResult of searchResults) {
            for (const poseId of searchResult.selectionIDs) {
                const frag = fragMap.get(poseId);
                if (frag) {
                    searchResult.addFrag(frag);
                } else {
                    console.warn(`Unknown fragment selectionID for ${searchResult.name}: ${poseId}`);
                }
            }
        }
    }

    /**
     * @param {object} result One result from the modification-selections packet from bfd-server
     */
    constructor(result) {
        super({
            origin: 'bfd-server',
            modType: result.modType,
            name: result.name,
            mwt: result.molecularWeight,
            // Bond vector atom names for assembly, only fragAtomName is actually sent by bfd-server
            srcDirectionAtomName: result.srcDirectionAtomName,
            fragAtomName: result.fragAtomName,
            fragDirectionAtomName: result.fragDirectionAtomName,
        });
        this.selectionIDs = [result.serialNumber];
        this.providedSolvation = result.solvationEnergy;
        this.providedExchemP = result.excessChemicalPotential;
    }

    /**
     *
     * @param {object} result One result from the modification-selections packet from bfd-server
     */
    addPoseId(result) {
        this.selectionIDs.push(result.serialNumber);
    }
}

export class DataServiceFragmentSearchResult extends FragmentSearchResult {
    /**
     * @param {import('BMapsModel').DecodedFragment} frag
     * @param {import('BMapsModel').MapCase[]} mapCasesInQuery
     * @param {{
     *  modType?: string,
     *  srcDirectionAtomName?: string,
     *  fragAtomName?: string,
     *  fragDirectionAtomName?: string,
     * }} obj Bond vector atom names and modification type for assembly
     */
    constructor(frag, mapCasesInQuery=[], obj={}) {
        super({
            origin: 'dataservice',
            modType: obj.modType || 'Near',
            name: frag.baseFrag.name,
            ddGs: frag.enSolv,
            mwt: calculateMolecularWeight(frag.getAtoms()),
            // Bond vector atom names for assembly
            srcDirectionAtomName: obj.srcDirectionAtomName,
            fragAtomName: obj.fragAtomName,
            fragDirectionAtomName: obj.fragDirectionAtomName,
        });

        // Initialize project case details for the multi-protein search case.
        // This is so LigandMod knows to display multi-protein results, even if
        // there aren't actually any search results found.
        // It would probably be better to track this information separately.
        for (const { projectCase } of mapCasesInQuery) {
            const perCaseInfo = this.getPerCaseInfo(projectCase);
            perCaseInfo.bestPoseInfo = new BestPoseBindingInfo();
        }

        this.addFrag(frag);
    }
}

/**
 * Collect information about a best pose from fragment search results.
 * This will be displayed and sortable in columns.
 */
export class BestPoseBindingInfo {
    // Since we're using scores rather than actual energies, Ian suggests
    // dividing by 1.36 which makes FE into log(IC50), closer to values they
    // might expect.
    static getBindingScore(bindingFE) {
        return bindingFE != null ? (bindingFE / 1.36) : null;
    }

    static getLigandEfficiency(bindingFE, nheavy) {
        return bindingFE != null && nheavy ? (-bindingFE/nheavy) : null;
    }

    static isBetter(aIn, bIn, compareField, higherIsBetter=false) {
        const a = aIn?.[compareField];
        const b = bIn?.[compareField];
        if (a == null) return false;
        if (b == null) return true;
        return higherIsBetter ? a > b : a < b;
    }

    constructor(frag) {
        this.bestPose = frag;
        if (frag) {
            // exchemP (and everything derived: binding score, ligand efficiency) is null for gen2
            // poses, where it isn't a meaningful quantity. enSolute / enSolv stay valid.
            const exchemPotential = poseExchemP(frag);
            const nheavy = countHeavyAtoms(frag.getAtoms());
            this.nheavy = nheavy;
            this.exchemPotential = exchemPotential;
            this.bindingFE = exchemPotential;
            this.enSolute = frag.enSolute;
            this.enSolv = frag.enSolv;
        }
    }

    get bindingScore() {
        return BestPoseBindingInfo.getBindingScore(this.bindingFE);
    }

    get ligandEfficiency() {
        return BestPoseBindingInfo.getLigandEfficiency(this.bindingFE, this.nheavy);
    }

    get ligandEfficiencyEn() {
        return BestPoseBindingInfo.getLigandEfficiency(this.enSolute, this.nheavy);
    }

    isBetterThan(other, compareField='bindingScore', higherIsBetter=false) {
        return BestPoseBindingInfo.isBetter(this, other, compareField, higherIsBetter);
    }

    meetsThreshold(threshold, compareField='bindingScore', higherIsBetter=false) {
        return higherIsBetter ? this[compareField] >= threshold : this[compareField] <= threshold;
    }
}

// Stuff for collecting statistics about the poses for a fragment distribution.
// This is used by column_definitions: getPoseStatisticsColumns and getFragStatFn.

/**
 * Collect values from a single fragment that will be used to calculate statistics
 */
function onePoseStatValues(frag) {
    return {
        enSolute: frag.enSolute,
        exchemPotential: poseExchemP(frag),
    };
}

/**
 * Collect values from all poses (categorized by case) that will be used to calculate statistics
 */
function collectPoseStatValues(searchResult) {
    const values = {};
    for (const frag of searchResult.frags) {
        const fragValues = onePoseStatValues(frag);

        const projectCase = frag.projectCase;
        if (!values[projectCase]) values[projectCase] = {};
        const projectCaseValues = values[projectCase];

        for (const [name, value] of Object.entries(fragValues)) {
            if (value == null) continue;
            if (projectCaseValues[name]) {
                projectCaseValues[name].push(value);
            } else {
                projectCaseValues[name] = [value];
            }
        }
    }
    return values;
}

/**
 * Collect values and calculate statistics for poses in the search result (separating by protein)
 */
function statsForOneSuggestionRow(searchResult) {
    const stats = {};
    const values = collectPoseStatValues(searchResult);

    for (const [projectCase, projectCaseValues] of Object.entries(values)) {
        const perCaseStats = {};
        for (const [name, vals] of Object.entries(projectCaseValues)) {
            const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
            const max = Math.max(...vals);
            const min = Math.min(...vals);
            perCaseStats[name] = { avg, max, min };
        }
        perCaseStats['poseCount'] = Object.values(projectCaseValues)
            .reduce((acc, nextVals) => Math.max(acc, nextVals.length), 0);
        stats[projectCase] = perCaseStats;
    }

    return stats;
}

export function sortFrags(frags, { sortPosesBy='position', bestPoseOrderField }={}) {
    if (frags.length === 0) return [];

    let unsortedFrags = [...frags];
    let sortedFrags = [];
    switch (sortPosesBy) {
        case 'position': {
            // Position sort starts with the "best pose", then greedily chains to the
            // nearest remaining pose so stepping through poses moves smoothly in space.
            // This greedy nearest-neighbour walk is O(n^2); for very large pose sets
            // (which can't realistically be browsed one-by-one anyway) skip the
            // chaining and keep input order, so a ~100-protein query doesn't spend
            // minutes here. The distance is inlined to avoid allocating a vector per
            // comparison (that allocation, x ~n^2, dominated the cost).
            const POSITION_SORT_MAX = 1500;
            const bestPoseFrag = FragmentSearchResult.findBestPose(unsortedFrags, {
                bestPoseOrderField,
            })?.bestPose;
            if (bestPoseFrag) {
                sortedFrags = [bestPoseFrag];
                unsortedFrags = unsortedFrags.filter((frag) => frag !== bestPoseFrag);
            }
            // Gate on the original pose count (frags), not unsortedFrags: the best pose was
            // just removed above, so unsortedFrags is one short and POSITION_SORT_MAX+1 poses
            // would otherwise slip past the cap into the O(n^2) walk.
            if (frags.length > POSITION_SORT_MAX) {
                sortedFrags = sortedFrags.concat(unsortedFrags);
                break;
            }
            while (unsortedFrags.length > 0) {
                let closestIndex = 0;
                let minDistSq = Infinity;
                const [nx, ny, nz] = sortedFrags[sortedFrags.length-1].translation;
                for (let i = 0; i<unsortedFrags.length; i++) {
                    const [tx, ty, tz] = unsortedFrags[i].translation;
                    const dx = tx-nx; const dy = ty-ny; const dz = tz-nz;
                    const distSq = dx*dx + dy*dy + dz*dz;
                    if (distSq < minDistSq) { minDistSq = distSq; closestIndex = i; }
                }
                sortedFrags.push(unsortedFrags[closestIndex]);
                unsortedFrags.splice(closestIndex, 1);
            }
            break;
        }
        case 'exchemPotential': // same ordering as bindingScore (just unscaled), gen2-aware
        case 'bindingScore':
            sortedFrags = [...unsortedFrags];
            // Sort by exchemP (null for gen2 poses), ordering gen2 poses last so they fall out of
            // exchemP/binding-score ranking, consistent with BestPoseBindingInfo.
            sortedFrags.sort((a, b) => {
                const aV = poseExchemP(a);
                const bV = poseExchemP(b);
                if (aV == null) return bV == null ? 0 : 1;
                if (bV == null) return -1;
                return aV - bV;
            });
            break;
        case 'poseName': {
            // Dev-only: order by pose spec string (e.g. "imidazole-2Me.118"), natural-numeric so
            // .2 sorts before .10. poseName is computed (getSpec), not a numeric frag field, so it
            // can't use the default subtraction path.
            sortedFrags = [...unsortedFrags];
            sortedFrags.sort((a, b) => {
                const aName = a.getSpec?.() ?? '';
                const bName = b.getSpec?.() ?? '';
                return aName.localeCompare(bName, undefined, { numeric: true });
            });
            break;
        }
        default:
            sortedFrags = [...unsortedFrags];
            if (unsortedFrags.some((f) => f[sortPosesBy] != null)) {
                sortedFrags.sort((a, b) => a[sortPosesBy] - b[sortPosesBy]);
            }
    }

    return sortedFrags;
}

// Various helpers for dealing with gen2 poses and exchemP

/**
 * Is this pose from a gen2-annealing simulation? For gen2 poses the excess chemical
 * potential is not a meaningful binding quantity, so binding score is suppressed.
 * @param {DecodedFragment} frag
 * @returns {boolean}
 */
export function isGen2Pose(frag) {
    return !!frag?.simulationMeta?.gen2Annealing;
}

/**
 * The pose's excess chemical potential, or null for gen2 poses (for which the exchemP is not a
 * meaningful quantity). This is the single gen2-aware source for everything derived from or
 * displaying exchemP: the "Ex. Chem. Potential" column, binding score, ligand efficiency,
 * best-pose selection, sort, filter, buckets, and pose statistics. Interaction energy (enSolute)
 * and enSolv are unaffected.
 * @param {DecodedFragment} frag
 * @returns {number|null}
 */
export function poseExchemP(frag) {
    return isGen2Pose(frag) ? null : frag?.exchemPotential;
}

/**
 * The energy used to filter/rank a pose: its binding-score exchemP when valid, otherwise
 * interaction energy (enSolute) — so gen2 poses (poseExchemP null) fall back to a meaningful
 * quantity. Returns null when neither is available. Shared by the fragment energy filter and the
 * energy-filter slider's data-driven lower bound so they stay consistent.
 * @param {DecodedFragment} frag
 * @returns {number|null}
 */
export function poseFilterEnergy(frag) {
    return poseExchemP(frag) ?? frag?.enSolute ?? null;
}

/**
 * Does a pose pass the fragment energy filter at the given threshold? gen2 poses are compared by
 * their fallback energy (poseFilterEnergy). A null threshold disables the filter, and a pose with
 * no energy at all is not filtered out (passes). Used by the fragment summary-map filter.
 * @param {DecodedFragment} frag
 * @param {number|null} filterValue threshold; null disables the filter
 * @returns {boolean}
 */
export function posePassesEnergyFilter(frag, filterValue) {
    if (filterValue == null) return true;
    const energy = poseFilterEnergy(frag);
    return energy == null || energy <= filterValue;
}

/**
 * Heritage energy-detail string for a fragment pose: excess chemical potential when it's a valid
 * binding quantity, otherwise (gen2 poses) interaction energy, which stays valid. Labeled
 * distinctly so the two aren't confused; reads "... N/A" when neither value is available.
 * @param {DecodedFragment} frag
 * @returns {string}
 */
export function fragEnergyDetailForHeritage(frag) {
    const exchemP = poseExchemP(frag);
    if (exchemP != null) return `Frag. FE (ExCP): ${exchemP.toFixed(1)}`;
    const enSolute = frag?.enSolute;
    return `Frag. Interaction Energy: ${enSolute != null ? enSolute.toFixed(1) : 'N/A'}`;
}

/**
 * Does any pose across the given search results have a valid (non-gen2) binding score?
 * Drives the result-set-wide gating of binding-score affordances: when this is false
 * (every pose is gen2), the binding-score toggle/highlight/pose-sort are disabled and
 * the primary scoring field falls back to interaction energy.
 * @param {{ frags?: DecodedFragment[] }[]} suggestions
 * @returns {boolean}
 */
export function anyPoseHasBindingScore(suggestions) {
    return !!suggestions?.some((s) => s.frags?.some((f) => poseExchemP(f) != null));
}
